import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, CheckIcon, CreditCardIcon, DownloadIcon, ReceiptTextIcon } from 'lucide-react';
import type { PlanId } from '../../types/plan';
import { useAccount } from '../../contexts/AccountContext';
import { annualPrice, getPlan, plans } from '../../data/plans';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { TextInput } from '../../components/ui/TextInput';
import { QuotaMeter } from '../../components/QuotaMeter';
import { EmptyState } from '../../components/EmptyState';
import { formatCurrency, formatDate } from '../../utils/format';
import { daysFromNow } from '../../utils/datetime';
import { servicesUsed as countServices } from '../../utils/health';
function monitorServices(ssl: boolean, uptime: boolean) {
  return (ssl ? 1 : 0) + (uptime ? 1 : 0);
}
export function Billing() {
  const {
    plan,
    planId,
    setPlanId,
    billingPeriod,
    setBillingPeriod,
    monitors,
    invoices,
    servicesUsed,
    serviceLimit,
    pauseMonitors
  } = useAccount();
  const [pendingPlan, setPendingPlan] = useState<PlanId | null>(null);
  const [keepIds, setKeepIds] = useState<string[]>([]);
  const [editingCard, setEditingCard] = useState(false);
  const [cardLast4, setCardLast4] = useState('4242');
  const [newCard, setNewCard] = useState('');
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const nextInvoiceDate = daysFromNow(billingPeriod === 'annual' ? 331 : 26);
  const nextAmount = billingPeriod === 'annual' ? annualPrice(plan) : plan.monthlyPrice;
  const keptServices = useMemo(() => countServices(monitors.filter(monitor => keepIds.includes(monitor.id))), [monitors, keepIds]);
  function choosePlan(nextId: PlanId) {
    if (nextId === planId) {
      return;
    }
    const target = getPlan(nextId);
    if (target.services >= servicesUsed) {
      setPlanId(nextId);
      setPendingPlan(null);
      setConfirmation(target.services > serviceLimit ? `You are on ${target.name}. ${target.services} services are available now.` : `You are on ${target.name}. Nothing was paused.`);
      return;
    }

    // Pre-select as many monitors as the smaller plan can hold, in list order.
    const preselected: string[] = [];
    let running = 0;
    monitors.forEach(monitor => {
      const cost = monitorServices(monitor.sslEnabled, monitor.uptimeEnabled);
      if (running + cost <= target.services) {
        preselected.push(monitor.id);
        running += cost;
      }
    });
    setKeepIds(preselected);
    setPendingPlan(nextId);
    setConfirmation(null);
  }
  function confirmDowngrade() {
    if (!pendingPlan) {
      return;
    }
    const paused = monitors.filter(monitor => !keepIds.includes(monitor.id)).map(monitor => monitor.id);
    pauseMonitors(paused);
    setPlanId(pendingPlan);
    setConfirmation(`You are on ${getPlan(pendingPlan).name}. ${paused.length} ${paused.length === 1 ? 'monitor was' : 'monitors were'} paused.`);
    setPendingPlan(null);
  }
  const target = pendingPlan ? getPlan(pendingPlan) : null;
  return <div className="space-y-5" data-id="element-796" data-mp="19xe9kxmxy34h">
      <div data-id="element-797" data-mp="19yftzspuk1yv">
        <h1 className="text-xl font-semibold tracking-tight text-ink" data-id="element-798" data-mp="19ypberwtt0sy">
          Billing
        </h1>
        <p className="mt-1 text-[13px] text-ink-muted" data-id="element-799" data-mp="h7npm7ymo70g">
          Your plan, your service allowance, and everything we have charged you.
        </p>
      </div>

      {confirmation && <div role="status" className="flex items-start gap-2.5 rounded-card border border-healthy-border bg-healthy-soft px-5 py-3.5" data-id="element-800" data-mp="v5cdlonzru9q">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-healthy" aria-hidden="true" data-id="element-801" data-mp="26laoly2qcqdl" />
          <p className="text-[13px] text-healthy-ink" data-id="element-802" data-mp="22n3916k05v3z">{confirmation}</p>
        </div>}

      <div className="grid gap-5 lg:grid-cols-[1.5fr_1fr] lg:items-start" data-id="element-803" data-mp="2xwzzzkoxxdz4">
        <Panel as="section" data-id="element-804" data-mp="31xjxipdfejls">
          <PanelHeader title="Current plan" action={<div className="inline-flex items-center gap-1 rounded-control border border-line-strong bg-canvas p-1" data-id="element-806" data-mp="36uaerg0yvl8s">
                {(['monthly', 'annual'] as const).map(option => <button key={option} type="button" onClick={() => setBillingPeriod(option)} aria-pressed={billingPeriod === option} className={`rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors duration-150 ease-snap ${billingPeriod === option ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink'}`} data-id="element-807" data-mp="3gt9pr597fsyy">
                    {option === 'monthly' ? 'Monthly' : 'Yearly'}
                  </button>)}
              </div>} data-id="element-805" data-mp="31yv22rep3vdy" />
          <div className="px-5 py-5" data-id="element-808" data-mp="1nwe5xzdet2hj">
            <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-809" data-mp="1nzjsp7ykfsa3">
              <div data-id="element-810" data-mp="1iycn9fjwb56l">
                <p className="text-2xl font-semibold text-ink" data-id="element-811" data-mp="1izxfuzmnge1t">{plan.name}</p>
                <p className="mt-1 text-[13px] text-ink-muted" data-id="element-812" data-mp="1e0u8v9n3zxr7">
                  {plan.tagline}
                </p>
              </div>
              <div className="text-right" data-id="element-813" data-mp="26nyjpv8vv6aw">
                <p className="tabular text-2xl font-semibold text-ink" data-id="element-814" data-mp="26ozv60tt9ut5">
                  {formatCurrency(nextAmount)}
                </p>
                <p className="text-xs text-ink-soft" data-id="element-815" data-mp="21ulx1qnrl9ks">
                  per {billingPeriod === 'annual' ? 'year' : 'month'}
                </p>
              </div>
            </div>

            <dl className="mt-5 grid gap-4 border-t border-line pt-4 sm:grid-cols-2" data-id="element-816" data-mp="2uekj6y4tni9a">
              <div data-id="element-817" data-mp="2pkztdtddtul6">
                <dt className="text-xs text-ink-soft" data-id="element-818" data-mp="2pkgb2bjtl46m">Next invoice</dt>
                <dd className="tabular mt-1 text-[13px] font-medium text-ink" data-id="element-819" data-mp="npis6vnupv4k">
                  {formatDate(nextInvoiceDate)} · {formatCurrency(nextAmount)}
                </dd>
              </div>
              <div data-id="element-820" data-mp="dyea3n2vpu5i">
                <dt className="text-xs text-ink-soft" data-id="element-821" data-mp="dx3alzggv8k7">Services in use</dt>
                <dd className="tabular mt-1 text-[13px] font-medium text-ink" data-id="element-822" data-mp="iwzcnt5lspx4">
                  {servicesUsed} of {serviceLimit} across {monitors.length}{' '}
                  domains
                </dd>
              </div>
            </dl>
          </div>
        </Panel>

        <div className="space-y-4" data-id="element-823" data-mp="3r6iu1er5t9jv">
          <QuotaMeter used={servicesUsed} limit={serviceLimit} planName={plan.name} showUpgrade={false} data-id="element-824" data-mp="3r7ahg2als0z2" />
          <Panel className="p-5" data-id="element-825" data-mp="1pe2zsm7ip7a0">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-ink" data-id="element-826" data-mp="1pew2nc3cvcmv">
              <CreditCardIcon className="h-4 w-4 text-ink-muted" aria-hidden="true" data-id="element-827" data-mp="1uc4l7dsf1ion" />
              Payment method
            </h2>
            {editingCard ? <div className="mt-4 space-y-3" data-id="element-828" data-mp="39xyfub2suscq">
                <Field label="New card number" htmlFor="replace-card" data-id="element-829" data-mp="3ol1c6efdyuyh">
                  <TextInput id="replace-card" inputMode="numeric" placeholder="4242 4242 4242 4242" className="tabular" value={newCard} onChange={event => setNewCard(event.target.value.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim())} data-id="element-830" data-mp="3one3hi71aho7" />
                </Field>
                <div className="flex gap-2" data-id="element-831" data-mp="2871frdfr1nfz">
                  <Button size="sm" onClick={() => {
                const digits = newCard.replace(/\s/g, '');
                if (digits.length === 16) {
                  setCardLast4(digits.slice(-4));
                  setNewCard('');
                  setEditingCard(false);
                  setConfirmation('Your card has been replaced.');
                }
              }} data-id="element-832" data-mp="288csw3v347jr">
                    Save card
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingCard(false)} data-id="element-833" data-mp="13hcpcmhp0no7">
                    Cancel
                  </Button>
                </div>
              </div> : <>
                <p className="tabular mt-3 text-[13px] text-ink" data-id="element-834" data-mp="1hcg7l33n1aug">
                  Visa ending {cardLast4} · expires 04/29
                </p>
                <p className="mt-1 text-xs text-ink-soft" data-id="element-835" data-mp="2sswoz1yr61jy">
                  Billed to {plan.name}, {billingPeriod}.
                </p>
                <Button variant="secondary" size="sm" className="mt-4" onClick={() => setEditingCard(true)} data-id="element-836" data-mp="h8p3vde87tg2">
                  Replace card
                </Button>
              </>}
          </Panel>
        </div>
      </div>

      <Panel as="section" data-id="element-837" data-mp="3ulhe49ob79us">
        <PanelHeader title="Change plan" description="Upgrades apply immediately and are prorated. Downgrades show you what would be paused first." level={2} data-id="element-838" data-mp="3ulr2xtxmbayw" />
        <div className="grid gap-3 px-5 py-5 md:grid-cols-3" data-id="element-839" data-mp="3lk14431ep9r3">
          {plans.map(option => {
          const current = option.id === planId;
          const selected = pendingPlan === option.id;
          return <div key={option.id} className={`flex flex-col rounded-card border px-4 py-4 transition-colors duration-150 ease-snap ${current ? 'border-brand bg-brand-soft' : selected ? 'border-warn-border bg-warn-soft' : 'border-line bg-surface'}`} data-id="element-840" data-mp="1jsxqr604th2u">
                <div className="flex items-baseline justify-between gap-2" data-id="element-841" data-mp="3lh0il7h8azpw">
                  <span className="text-sm font-semibold text-ink" data-id="element-842" data-mp="3gkjkbkgbf4z3">
                    {option.name}
                  </span>
                  <span className="tabular text-sm font-medium text-ink" data-id="element-843" data-mp="2sscpipq8n61s">
                    ${option.monthlyPrice}/mo
                  </span>
                </div>
                <p className="tabular mt-1 text-xs text-ink-muted" data-id="element-844" data-mp="32l0nvowv9i2m">
                  {option.services} services ·{' '}
                  {option.pingOptions[option.pingOptions.length - 1] === 1 ? '1 min pings' : `${option.pingOptions[option.pingOptions.length - 1]} min pings`}
                </p>
                <p className="mt-2 flex-1 text-xs leading-relaxed text-ink-muted" data-id="element-845" data-mp="klregjh0yqny">
                  {option.tagline}
                </p>
                <Button size="sm" variant={current ? 'secondary' : 'primary'} className="mt-4 w-full" disabled={current} onClick={() => choosePlan(option.id)} data-id="element-846" data-mp="pfn9bd1zrxew">
                  {current ? 'Current plan' : option.services < servicesUsed ? `Downgrade to ${option.name}` : `Switch to ${option.name}`}
                </Button>
              </div>;
        })}
        </div>

        {target && <div className="border-t border-line bg-warn-soft px-5 py-5" data-id="element-847" data-mp="z9vs39180bzv">
            <div className="flex items-start gap-3" data-id="element-848" data-mp="3f233gb6oeary">
              <AlertTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-warn" aria-hidden="true" data-id="element-849" data-mp="3k1xqu4r8si89" />
              <div className="min-w-0 flex-1" data-id="element-850" data-mp="2zyjjq0oycthr">
                <h3 className="text-sm font-semibold text-warn-ink" data-id="element-851" data-mp="2vy7xijgxyl46">
                  {target.name} only holds {target.services} services — you are
                  using {servicesUsed}
                </h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-warn-ink/85" data-id="element-852" data-mp="2m8odvblemn89">
                  Choose which monitors to keep running. Anything you leave
                  unchecked is paused, keeps its history, and can be switched
                  back on if you move up again.
                </p>

                <ul className="mt-4 space-y-2" data-id="element-853" data-mp="28858ydc8jxab">
                  {monitors.map(monitor => {
                const cost = monitorServices(monitor.sslEnabled, monitor.uptimeEnabled);
                const kept = keepIds.includes(monitor.id);
                const wouldExceed = !kept && keptServices + cost > target.services;
                return <li key={monitor.id} data-id="element-854" data-mp="8zm5j5h9h8sq">
                        <label className={`flex items-center gap-3 rounded-control border bg-surface px-3.5 py-2.5 ${wouldExceed ? 'border-line opacity-60' : 'border-line-strong'}`} data-id="element-855" data-mp="e09wxs9ejrnh">
                          <input type="checkbox" className="h-3.5 w-3.5 rounded border-line-strong text-brand focus:ring-brand" checked={kept} disabled={wouldExceed || cost === 0} onChange={() => setKeepIds(current => kept ? current.filter(id => id !== monitor.id) : [...current, monitor.id])} data-id="element-856" data-mp="3macryidksdig" />
                          <span className="min-w-0 flex-1" data-id="element-857" data-mp="192bq03fi6xeb">
                            <span className="block truncate text-[13px] font-medium text-ink" data-id="element-858" data-mp="1nyt3p1b60o41">
                              {monitor.domain}
                            </span>
                            <span className="block text-xs text-ink-soft" data-id="element-859" data-mp="1iyewtmlavk7l">
                              {monitor.sslEnabled && monitor.uptimeEnabled ? 'SSL + uptime' : monitor.sslEnabled ? 'SSL only' : monitor.uptimeEnabled ? 'Uptime only' : 'Already paused'}
                            </span>
                          </span>
                          <span className="tabular shrink-0 text-xs text-ink-muted" data-id="element-860" data-mp="2bonezefu64m6">
                            {cost} {cost === 1 ? 'service' : 'services'}
                          </span>
                        </label>
                      </li>;
              })}
                </ul>

                <p className="tabular mt-3 text-[13px] font-medium text-warn-ink" data-id="element-861" data-mp="2lciy9g3f2cop">
                  Keeping {keptServices} of {target.services} services ·{' '}
                  {monitors.length - keepIds.length} would be paused
                </p>

                <div className="mt-4 flex flex-wrap gap-2" data-id="element-862" data-mp="2bkvbhsht1jtk">
                  <Button size="sm" onClick={confirmDowngrade} disabled={keptServices > target.services} data-id="element-863" data-mp="2gd6b0u8mv3ll">
                    Move to {target.name} and pause the rest
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setPendingPlan(null)} data-id="element-864" data-mp="1snc40tazw999">
                    Keep {plan.name}
                  </Button>
                </div>
              </div>
            </div>
          </div>}
      </Panel>

      <Panel as="section" className="overflow-hidden" data-id="element-865" data-mp="3ujt90gfawnl0">
        <PanelHeader title="Invoices" description="Receipts are also emailed the moment a payment succeeds." level={2} data-id="element-866" data-mp="3uj1frc96f1xw" />
        {invoices.length === 0 ? <EmptyState icon={ReceiptTextIcon} title="No invoices yet" body="Your first receipt will appear here as soon as the first payment goes through." data-id="element-867" data-mp="3licz09seenhb" /> : <div className="overflow-x-auto" data-id="element-868" data-mp="d44dtms7gyz6">
            <table className="w-full min-w-[560px] border-collapse text-left" data-id="element-869" data-mp="d2t8vjn0xl6h">
              <caption className="sr-only" data-id="element-870" data-mp="8487vhzy6xgz">Invoice history</caption>
              <thead data-id="element-871" data-mp="2xubv5e65zv88">
                <tr className="border-b border-line bg-canvas" data-id="element-872" data-mp="2xuvcotrwkhlq">
                  <th scope="col" className="px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-ink-soft" data-id="element-873" data-mp="2svig3hb8bw5y">
                    Invoice
                  </th>
                  <th scope="col" className="px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-ink-soft" data-id="element-874" data-mp="2iter5qlhjzrc">
                    Date
                  </th>
                  <th scope="col" className="px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-ink-soft" data-id="element-875" data-mp="2soq7gdcjlf17">
                    Plan
                  </th>
                  <th scope="col" className="px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-ink-soft" data-id="element-876" data-mp="31ibkns926o8i">
                    Amount
                  </th>
                  <th scope="col" className="px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-ink-soft" data-id="element-877" data-mp="36izcuh90xb4b">
                    Receipt
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-878" data-mp="1h2rk6pes1i4i">
                {invoices.map(invoice => <tr key={invoice.id} className="border-b border-line last:border-b-0" data-id="element-879" data-mp="1h4kse951gaem">
                    <td className="tabular px-5 py-3 text-[13px] font-medium text-ink" data-id="element-880" data-mp="1m3fa17r9rsx5">
                      {invoice.number}
                    </td>
                    <td className="tabular px-5 py-3 text-[13px] text-ink-muted" data-id="element-881" data-mp="3j0juwhw39ea0">
                      {formatDate(invoice.issuedAt)}
                    </td>
                    <td className="px-5 py-3 text-[13px] text-ink-muted" data-id="element-882" data-mp="1l3c7f17esyu">
                      {invoice.planName} · {invoice.periodLabel}
                    </td>
                    <td className="tabular px-5 py-3 text-[13px] text-ink" data-id="element-883" data-mp="350ivz7zdwgx6">
                      {formatCurrency(invoice.amount)}
                    </td>
                    <td className="px-5 py-3 text-right" data-id="element-884" data-mp="3e0fbaizvw3fk">
                      <button type="button" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-brand transition-colors duration-150 ease-snap hover:text-brand-hover" data-id="element-885" data-mp="3e20689q41ody">
                        <DownloadIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-886" data-mp="p7tcobxwmfhc" />
                        PDF
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Panel>
    </div>;
}